import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import type { PreviewIncidentReportResponse } from '@roadfootage/api-client'

import { useSessionStore } from '@/stores/session'
import { useIncidentSubmissionStore } from '@/stores/incident-submission'
import { useFileUploadStore } from '@/stores/file-upload'
import { useBountyResponseStore } from '@/stores/bounty-response'

/** The preview URL that starts "share yours" on arrival, used as the sign-in return target. */
export const shareYoursTarget = (reportId: string): string => `/preview/${reportId}?share=1`

/**
 * "Have footage of this incident? Share yours": opens the upload wizard prefilled from the original report and carrying the
 * relation intent. Anonymous visitors are sent to sign in first and come back to the preview page.
 */
export function useShareYourFootage() {
  const sessionStore = useSessionStore()
  const { isLoggedIn } = storeToRefs(sessionStore)
  const incidentStore = useIncidentSubmissionStore()
  const fileUploadStore = useFileUploadStore()
  const bountyResponseStore = useBountyResponseStore()
  const router = useRouter()

  async function startSharing(report: PreviewIncidentReportResponse): Promise<void> {
    if (!report.id) return
    if (!isLoggedIn.value) {
      await sessionStore.loginWithRedirect({ appState: { target: shareYoursTarget(report.id) } })
      return
    }
    incidentStore.$reset()
    fileUploadStore.$reset()
    // A leftover bounty response must not ride along with this report.
    bountyResponseStore.cancelResponse()
    incidentStore.prefillFromRelatedReport(report)
    await router.push({ name: 'upload' })
  }

  return { isLoggedIn, startSharing }
}
